import { useMemo, useRef, useState } from "react";
import { Segmented, Table, TableScroll } from "../ui";
import { ChartTooltip, ChartTooltipRow } from "./ChartTooltip";
import { useWidth } from "./useWidth";

export interface Series {
  id: string;
  label: string;
  /** CSS color, e.g. var(--series-1). */
  color: string;
  values: number[];
  /** De-emphasized comparison series (e.g. previous period). */
  muted?: boolean;
}

/** A vertical marker at a bucket index, e.g. a release. */
export interface Marker {
  index: number;
  label: string;
}

/** Rounded tick step ("nice numbers") for a max value. */
export function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) ticks.push(v);
  if (ticks[ticks.length - 1]! < max) ticks.push(ticks[ticks.length - 1]! + step);
  return ticks;
}

interface LineChartProps {
  series: Series[];
  /** X-axis label per index (`fmtBucket` for time buckets). Also the table's row headers. */
  labels: string[];
  height?: number;
  /** The metric's `lib/format` helper. Used for the axis, the tooltip and the table. */
  format?: (v: number) => string;
  /** Optional per-series label for each index (e.g. previous period dates). */
  tooltipLabels?: Record<string, string[]>;
  /** Vertical markers (releases). Also list them as text near the chart: the SVG is one image to assistive tech. */
  markers?: Marker[];
}

/**
 * A line chart with its text alternative built in: a Chart / Table `Segmented` above it swaps the
 * SVG for `ChartDataTable` (same height, so nothing below jumps). The SVG is one image to assistive
 * tech, and hover is mouse-only; the table gives screen reader and keyboard users every value.
 */
export function LineChart({
  table,
  ...chart
}: LineChartProps & {
  /** The table view: `caption` says what the rows are and their order; `rowHeader` names the first column ("Day"). */
  table: { caption: string; rowHeader: string };
}) {
  const [view, setView] = useState("chart");
  const height = chart.height ?? 240;
  return (
    <div>
      <div className="flex justify-end px-1 pb-1">
        <Segmented
          aria-label="Show data as"
          items={[
            { value: "chart", label: "Chart" },
            { value: "table", label: "Table" },
          ]}
          value={view}
          onChange={setView}
        />
      </div>
      {view === "table" ? (
        <div style={{ height }}>
          <ChartDataTable {...table} rows={chart.labels} series={chart.series} format={chart.format} tooltipLabels={chart.tooltipLabels} />
        </div>
      ) : (
        <LinePlot {...chart} />
      )}
    </div>
  );
}

function LinePlot({ series, labels, height = 240, format = (v) => v.toLocaleString(), tooltipLabels, markers = [] }: LineChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const n = labels.length;
  const pad = { top: 12, right: 12, bottom: 26, left: 44 };
  const w = Math.max(0, width - pad.left - pad.right);
  const h = height - pad.top - pad.bottom;

  const { ticks, yMax } = useMemo(() => {
    const max = Math.max(0, ...series.flatMap((s) => s.values));
    const t = niceTicks(max);
    return { ticks: t, yMax: t[t.length - 1] ?? 1 };
  }, [series]);

  const x = (i: number) => (n <= 1 ? w / 2 : (i / (n - 1)) * w);
  const y = (v: number) => h - (v / yMax) * h;

  const path = (vals: number[]) => vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");

  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(w / 90))));

  const onMove = (e: React.PointerEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || n === 0) return;
    const px = e.clientX - rect.left - pad.left;
    setHover(Math.max(0, Math.min(n - 1, Math.round((px / Math.max(1, w)) * (n - 1)))));
  };

  const primary = series.find((s) => !s.muted);

  return (
    <div ref={ref} className="relative w-full select-none" style={{ height }}>
      {width > 0 && (
        <svg
          ref={svgRef}
          width={width}
          height={height}
          role="img"
          aria-label={`${series.map((s) => s.label).join(", ")} over time`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          <g transform={`translate(${pad.left},${pad.top})`}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={0} x2={w} y1={y(t)} y2={y(t)} stroke={t === 0 ? "var(--axis)" : "var(--grid)"} strokeWidth={1} />
                <text x={-8} y={y(t)} dy="0.32em" textAnchor="end" className="tabular" fontSize={11} fill="var(--ink-3)">
                  {format(t)}
                </text>
              </g>
            ))}
            {labels.map((l, i) =>
              i % labelEvery === 0 ? (
                <text key={i} x={x(i)} y={h + 18} textAnchor={i === 0 && n > 1 ? "start" : "middle"} fontSize={11} fill="var(--ink-3)">
                  {l}
                </text>
              ) : null,
            )}
            {primary && n > 1 && (
              <path d={`${path(primary.values)}L${x(n - 1)},${h}L${x(0)},${h}Z`} fill={primary.color} opacity={0.1} />
            )}
            {series.map((s) => (
              <path
                key={s.id}
                d={path(s.values)}
                fill="none"
                stroke={s.muted ? "var(--prev)" : s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
            {markers.map((m, i) => (
              <g key={i} aria-hidden>
                <line x1={x(m.index)} x2={x(m.index)} y1={0} y2={h} stroke="var(--ink-3)" strokeWidth={1} strokeDasharray="3 3" />
                <path d={`M${x(m.index)},-2 l4,4 l-4,4 l-4,-4 Z`} fill="var(--ink-2)" />
              </g>
            ))}
            {hover !== null && (
              <g>
                <line x1={x(hover)} x2={x(hover)} y1={0} y2={h} stroke="var(--axis)" strokeWidth={1} />
                {series.map((s) => (
                  <circle
                    key={s.id}
                    cx={x(hover)}
                    cy={y(s.values[hover] ?? 0)}
                    r={4.5}
                    fill={s.muted ? "var(--prev)" : s.color}
                    stroke="var(--surface)"
                    strokeWidth={2}
                  />
                ))}
              </g>
            )}
          </g>
        </svg>
      )}
      {hover !== null && width > 0 && (
        <ChartTooltip
          className="top-2 min-w-40"
          style={x(hover) + pad.left > width / 2 ? { right: width - x(hover) - pad.left + 12 } : { left: x(hover) + pad.left + 12 }}
        >
          {markers
            .filter((m) => m.index === hover)
            .map((m, i) => (
              <div key={`m${i}`} className="flex items-center gap-1.5 pb-1 font-medium text-ink">
                <span className="inline-block h-2 w-2 rotate-45 bg-ink-2" aria-hidden />
                {m.label}
              </div>
            ))}
          {series.map((s) => (
            <ChartTooltipRow
              key={s.id}
              swatch={<span className="inline-block h-0.5 w-3 rounded-sm" style={{ background: s.muted ? "var(--prev)" : s.color }} />}
              label={tooltipLabels?.[s.id]?.[hover] ?? labels[hover]}
              value={format(s.values[hover] ?? 0)}
            />
          ))}
        </ChartTooltip>
      )}
    </div>
  );
}

/** A legend swatch: `square` for areas and bars, `line` for line series, `dot` for points, `rule` (dashed upright) for a reference mark. */
export type LegendShape = "square" | "line" | "dot" | "rule";

function Swatch({ shape, color }: { shape: LegendShape; color: string }) {
  if (shape === "rule") return <span className="inline-block h-2.5 border-l-[1.5px] border-dashed" style={{ borderColor: color }} />;
  const cls = { square: "h-2 w-2 rounded-sm", line: "h-0.5 w-3 rounded", dot: "size-2 rounded-full" }[shape];
  return <span className={`inline-block ${cls}`} style={{ background: color }} />;
}

/**
 * Color key for a chart's series. Hidden from screen readers: the chart's Table view carries the
 * same data. `shape` sets every swatch, or a series can set its own. `inline`
 * drops the card padding, for a legend placed inside a row that brings its own.
 */
export function ChartLegend({
  series,
  shape = "square",
  inline,
}: {
  series: (Pick<Series, "id" | "label" | "color"> & { shape?: LegendShape })[];
  shape?: LegendShape;
  inline?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-2 ${inline ? "" : "px-4 pb-3"}`} aria-hidden>
      {series.map((s) => (
        <span key={s.id} className="flex items-center gap-1.5">
          <Swatch shape={s.shape ?? shape} color={s.color} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

/**
 * A chart's numbers as a table: one row per point (oldest first), one column per series, the
 * comparison series last. Fills its parent's height and scrolls inside it. `LineChart` renders it
 * for its Table view; use it directly only for a chart without that toggle.
 */
export function ChartDataTable({
  caption,
  rowHeader,
  rows,
  series,
  format = (v) => v.toLocaleString(),
  tooltipLabels,
}: {
  caption: string;
  rowHeader: string;
  rows: string[];
  series: Pick<Series, "id" | "label" | "values" | "muted">[];
  format?: (v: number) => string;
  /** Per-series label for each row (e.g. the previous period's date), shown after the value. */
  tooltipLabels?: Record<string, string[]>;
}) {
  const cols = [...series.filter((s) => !s.muted), ...series.filter((s) => s.muted)];
  return (
    <TableScroll label={caption} className="h-full overflow-y-auto">
      <Table caption={caption} className="tabular">
        <thead className="sticky top-0 bg-surface">
          <tr>
            <th scope="col">{rowHeader}</th>
            {cols.map((s) => (
              <th key={s.id} scope="col" className="text-right">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <th scope="row" className="text-ink-2">
                {r}
              </th>
              {cols.map((s) => (
                <td key={s.id} className="text-right">
                  <span className={s.muted ? "text-ink-2" : "font-medium text-ink"}>{format(s.values[i] ?? 0)}</span>
                  {tooltipLabels?.[s.id]?.[i] && <span className="ml-1.5 text-xs text-ink-3">{tooltipLabels[s.id]![i]}</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
}
